import type { ReactNode } from "react";
import { cn } from "./cn";
import { marka } from "./marka";

/**
 * SİSTEM SAYFASI — hata, kimlik ve durum ekranlarının ortak kabuğu
 *
 * ================================================================
 * ON EKRAN, TEK BİLEŞEN
 * ================================================================
 * 404 · 403 · 500 · bakım · çevrimdışı · desteklenmeyen tarayıcı ·
 * hesap kilitli · oturum süresi doldu · çıkış yapıldı · şifremi
 * unuttum. Onunun da yapısı aynı: ızgara + rozet + başlık + açıklama
 * + eylemler + alt not.
 *
 * Teslim notunun kendi cümlesi bunları bir aile sayıyor:
 * *"SİSTEM SAYFALARI — hepsi AYNI DİLİ konuşuyor. Her biri küçük bir
 * program ızgarası gösteriyor ve hata o ızgarada ANLATILIYOR: eksik
 * blok, kilitli blok, kaymış sütun. Soyut bir illüstrasyon değil;
 * kullanıcının zaten tanıdığı geometri."*
 *
 * On ayrı ekran yazsaydık, biri güncellenir dokuzu eskirdi — ve
 * kullanıcı "bu hangi sistem" diye sorardı.
 *
 * ================================================================
 * IZGARA DEKORATİF DEĞİL, ANLAMLI
 * ================================================================
 * Yedi dolu blok haftalık programın kendisi. Sekizinci blok DURUMU
 * anlatıyor ve ekrandan ekrana değişiyor: 404'te aranan boş yer
 * (kesikli, nabız gibi), 403'te kilitli yer, 500'de kaymış sütun.
 *
 * `aria-hidden`: ekran okuyucuya geometri anlatmanın anlamı yok.
 * Bilgi başlıkta ve açıklamada, ikisi de metin.
 */

export type SistemIsareti = "arayan" | "kilitli" | "kirik" | "bekleyen" | "yok";

export interface SistemSayfasiProps {
  /** Küçük rozet — "404", "PLANLI BAKIM" gibi. */
  rozet: string;
  baslik: string;
  aciklama: ReactNode;
  /** Izgaranın sekizinci bloğu neyi anlatıyor. */
  isaret?: SistemIsareti;
  /** Düğmeler. */
  eylemler?: ReactNode;
  /**
   * Üstten çizgili alt not.
   *
   * Bu alan çizimde her sayfada var ve hepsinde AYNI işi yapıyor:
   * kullanıcının yanlış çıkaracağı sonucu önceden düzeltiyor.
   * "Bu bir yetki sorunu DEĞİL" gibi.
   */
  altNot?: ReactNode;
  /** Ek içerik — form alanları gibi (şifremi unuttum). */
  children?: ReactNode;
}

/** Yedi dolu blok: konum, boyut ve saydamlık çizimden alındı. */
const BLOKLAR: ReadonlyArray<{ sol: number; ust: number; yuk: number; saydam: number }> = [
  { sol: 0, ust: 51, yuk: 95, saydam: 0.9 },
  { sol: 51, ust: 0, yuk: 44, saydam: 0.35 },
  { sol: 51, ust: 153, yuk: 44, saydam: 0.55 },
  { sol: 102, ust: 51, yuk: 146, saydam: 0.85 },
  { sol: 153, ust: 0, yuk: 95, saydam: 0.45 },
  { sol: 153, ust: 153, yuk: 44, saydam: 0.3 },
  { sol: 204, ust: 102, yuk: 95, saydam: 0.7 },
];

function Izgara({ isaret }: { isaret: SistemIsareti }) {
  return (
    <div aria-hidden="true" className="relative hidden h-[197px] w-[248px] shrink-0 sm:block">
      {BLOKLAR.map((b, i) => (
        <div
          key={i}
          className="bg-marka-600 absolute w-11 rounded-[6px]"
          style={{
            left: b.sol,
            top: b.ust,
            height: b.yuk,
            opacity: b.saydam,
            // Sirali beliriş — çizimde 120ms'den başlayıp 55ms artıyor.
            animation: `sistemBelir var(--sure-normal) var(--ease-cikis) ${120 + i * 55}ms backwards`,
          }}
        />
      ))}

      {isaret !== "yok" && (
        <div
          className={cn(
            "absolute top-0 left-[102px] h-11 w-11 rounded-[6px]",
            isaret === "arayan" && "border-notr-400 border-2 border-dashed",
            isaret === "kilitli" && "border-notr-400 bg-notr-100 border-2 border-solid",
            isaret === "kirik" && "border-hata-500/40 bg-hata-500/10 border-2 border-solid",
            isaret === "bekleyen" && "border-uyari-500/50 bg-uyari-500/15 border-2 border-solid",
          )}
          style={
            isaret === "arayan" ? { animation: "sistemArar 2.8s ease-in-out infinite" } : undefined
          }
        />
      )}
    </div>
  );
}

export function SistemSayfasi({
  rozet,
  baslik,
  aciklama,
  isaret = "yok",
  eylemler,
  altNot,
  children,
}: SistemSayfasiProps) {
  return (
    <div className="bg-tuval flex min-h-dvh flex-col">
      {/*
        ================================================================
        ÜST BANT MARKAYI TAŞIYOR, GEZİNMEYİ TAŞIMIYOR
        ================================================================
        Bu sayfaların çoğunda kullanıcı ya oturum dışında ya da
        gidebileceği bir yer yok. Menü çizmek, tıklanınca yine buraya
        dönen bağlantılar göstermek olurdu.
      */}
      <header
        className="flex h-[58px] shrink-0 items-center gap-[11px] px-10"
        style={{ background: "var(--gradyan-kabuk)" }}
      >
        {/*
          MARKA `marka.ts`TEN OKUNUR — BURAYA YAZILMAZ.

          Burada bir sure `K` harfi ve `[KISA AD]` metni ELLE yaziliydi.
          `marka.ts`in basligi "firma adinin kodda gectigi TEK yer"
          diyor; bu bilesen o tek yeri atliyordu.

          Canli gorundu (04.09.2026): marka artik tanimli ve giris
          ekrani dogru sekilde "M · Mentor" yaziyordu, ama 404 sayfasi
          hala "K · [KISA AD]" diyordu. Ayni uygulamada iki farkli
          cevap.

          Bedeli, degdigi yer yuzunden buyuk: bu kabuk ON ekranin
          ortak kabugu (404 · 403 · 500 · bakim · cevrimdisi ·
          desteklenmeyen tarayici · hesap kilitli · oturum doldu ·
          cikis yapildi · sifremi unuttum) — yani kullanicinin ZATEN
          bir sorun yasadigi anlarin hepsi. Kullanici tam da sistemden
          suphelendigi anda tamamlanmamis bir yer tutucu goruyordu.
        */}
        <div className="rounded-alan bg-marka-600 text-notr-0 grid size-[30px] place-items-center text-[13px] font-bold">
          {marka.kisaAd.trim().charAt(0).toLocaleUpperCase("tr-TR")}
        </div>
        <span className="text-notr-0 text-sm font-semibold">{marka.kisaAd}</span>
      </header>

      <main className="flex flex-1 items-center justify-center px-6 py-14 sm:px-10">
        <div className="flex max-w-[1000px] flex-col items-center gap-10 sm:flex-row sm:gap-16">
          <Izgara isaret={isaret} />

          <div className="flex max-w-[470px] flex-col gap-[17px]">
            <span className="rounded-rozet bg-notr-50 text-notr-700 ring-notr-200 self-start px-3 py-1 text-[12.5px] font-semibold tracking-[0.06em] uppercase ring-1 ring-inset">
              {rozet}
            </span>

            <h1 className="text-notr-900 text-[31px] leading-[1.2] font-semibold tracking-[-0.022em]">
              {baslik}
            </h1>

            <div className="text-notr-700 text-[15px] leading-[1.68]">{aciklama}</div>

            {children}

            {eylemler && <div className="flex flex-wrap gap-[11px] pt-1">{eylemler}</div>}

            {altNot && (
              <div className="border-notr-300 text-notr-600 border-t pt-3.5 text-[12.5px] leading-[1.6]">
                {altNot}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
